import { useCallback, useMemo } from 'react';
import { DrawerBody, DrawerFooter, DrawerHeader, DrawerProps, Flex, Box, Drawer } from '@cad-arc';
import { observer } from 'mobx-react-lite';
import { useForm, useWatch } from 'react-hook-form';
import { useQuery } from '@tanstack/react-query';
import { useApiClient } from '@m43/core';
import { CallSignOption, EditUnitForm, EditUnitFormType, EditUnitResource } from '@mark43/cad-api';
import { LogOnEditUnitForm } from './log-on-edit-unit-form/log-on-edit-unit-form';
import { useDrawerControls } from '../drawers/components/drawer-context-provider';
import { IntegratedFormProvider } from '../forms/integrated-form-provider';
import { FormBody } from '../forms/integrated-form-components/form-body';
import { FormCancelButton } from '../forms/integrated-form-components/form-cancel-button';
import { FormSaveButton } from '../forms/integrated-form-components/form-save-button';
import { mergeQueries } from '../forms/helpers/merge-queries';
import { EDIT_UNIT_DEFAULT_FORM_VALUES } from './constants';
import { useEditUnitFormMutation } from './hooks/use-edit-unit-form-mutation';
import { useEditUnitFormState } from './hooks/use-edit-unit-form-state';
import { isValidSelectedPersonnel } from './helpers';

export const LogOnEditUnitDrawer = observer(function LogOnEditUnitDrawer({
    unitId,
    portalProps,
}: {
    unitId?: number;
    portalProps?: DrawerProps['portalProps'];
}): React.ReactElement {
    const drawerControls = useDrawerControls();
    const closeDrawer = useMemo(() => {
        return () => {
            drawerControls.close();
        };
    }, [drawerControls]);

    return (
        <Drawer
            variant="overlay"
            isOpen={true}
            size="lg"
            onClose={closeDrawer}
            portalProps={portalProps}
        >
            <LogOnEditUnitDrawerContent unitId={unitId} closeDrawer={closeDrawer} />
        </Drawer>
    );
});

type LogOnEditUnitDrawerType = typeof LogOnEditUnitDrawer;
export type { LogOnEditUnitDrawerType };

function LogOnEditUnitDrawerContent({
    unitId: initialUnitId,
    closeDrawer,
}: {
    unitId?: number;
    closeDrawer: () => void;
}): React.ReactElement {
    const apiClient = useApiClient();

    const form = useForm<EditUnitFormType>({
        defaultValues: {
            ...EDIT_UNIT_DEFAULT_FORM_VALUES,
            callSign: initialUnitId ? { value: initialUnitId } : null,
        },
        mode: 'onBlur',
    });

    const selectedCallSign: CallSignOption | null = useWatch<EditUnitFormType, 'callSign'>({
        name: 'callSign',
        control: form.control,
    });

    // used if loading an existing unit and when a call sign is selected
    const existingUnitQuery = useQuery(
        ['edit-unit-view', selectedCallSign?.value, initialUnitId] as const,
        ({ queryKey }) => {
            return apiClient
                .request<EditUnitResource.GetEditUnitView>({
                    method: 'POST',
                    url: `edit_unit/${queryKey[1]}/`,
                    data: {
                        formOpenedForUnitId: queryKey[2],
                    },
                })
                .then((resp) => {
                    return resp.data.data;
                });
        },
        {
            enabled: !!selectedCallSign?.value,
        }
    );

    const existingUnitData = existingUnitQuery.data;

    // used if logging on a unit
    const logOnUnitQuery = useQuery(
        ['edit-unit-options', initialUnitId],
        () => {
            return apiClient
                .request<EditUnitResource.BootstrapOld>({
                    method: 'GET',
                    url: 'edit_unit/',
                })
                .then((resp) => {
                    return resp.data.data;
                });
        },
        {
            enabled: !initialUnitId,
        }
    );

    const { data: editUnitOptions } = logOnUnitQuery;

    const {
        hasLoadedOnce,
        formFieldOptions,
        statics,
        setUserChangedStaffCount,
    } = useEditUnitFormState({
        form,
        existingUnitData,
        editUnitOptions,
        isLogOnUnitForm: !initialUnitId,
    });

    const mutation = useEditUnitFormMutation({ onMutationSuccess: closeDrawer });

    const onSubmit = useCallback(
        (props: EditUnitFormType): void => {
            mutation.mutate({
                ...props,
                staffCount: Number(props.staffCount),
                unitId: initialUnitId,
                personnel: props.personnel.filter(isValidSelectedPersonnel),
            } as EditUnitForm);
        },
        [mutation, initialUnitId]
    );

    // This form has two possible loading queries. We merge these queries here to fit FormStateProvider's paradigm.
    const query = useMemo(() => {
        const mergedQueries = mergeQueries(existingUnitQuery, logOnUnitQuery);
        return {
            isInitialLoading: hasLoadedOnce ? false : mergedQueries.isInitialLoading,
            isError: mergedQueries.isError,
        };
    }, [existingUnitQuery, logOnUnitQuery, hasLoadedOnce]);

    return (
        <IntegratedFormProvider query={query} mutation={mutation} form={form} onSubmit={onSubmit}>
            <Flex height="100%" direction="column" data-testid="log-on-edit-unit-drawer">
                <DrawerHeader>{initialUnitId ? 'Edit Unit' : 'Log On Unit'}</DrawerHeader>
                <DrawerBody>
                    <FormBody>
                        <LogOnEditUnitForm
                            form={form}
                            formFieldOptions={formFieldOptions}
                            statics={statics}
                            isEditUnit={!!initialUnitId}
                            setUserChangedStaffCount={setUserChangedStaffCount}
                        />
                    </FormBody>
                </DrawerBody>
                <DrawerFooter>
                    <Box mr={3}>
                        <FormCancelButton onClick={closeDrawer} />
                    </Box>
                    <FormSaveButton data-testid="save-log-on-edit-unit-drawer" />
                </DrawerFooter>
            </Flex>
        </IntegratedFormProvider>
    );
}
